// Full Calendar Plugins
import dayGridPlugin from '@fullcalendar/daygrid'
import timeGridPlugin from '@fullcalendar/timegrid'
import listPlugin from '@fullcalendar/list'
import interactionPlugin from '@fullcalendar/interaction'

import { isArray, isFunction } from 'lodash'

import ptBRLocale from '@fullcalendar/core/locales/pt-br'

// Notification
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

// eslint-disable-next-line object-curly-newline
import { ref, computed, reactive, watch, onMounted } from '@vue/composition-api'
import store from '@/store/store'

import moment from 'moment'


export default function userCalendar(composition = {}) {
  const now = moment()
  now.add(1, 'months')

  // Use toast
  const toast = useToast()
  // ------------------------------------------------
  // refCalendar
  // ------------------------------------------------
  const refCalendar = ref(null)

  // ------------------------------------------------
  // calendarApi
  // ------------------------------------------------
  let calendarApi = null
  onMounted(() => {
    calendarApi = refCalendar.value.getApi()
  })

  // ------------------------------------------------
  // calendars
  // ------------------------------------------------
  const calendarsColor = {
    Business: 'primary',
    Holiday: 'success',
    Personal: 'danger',
    Family: 'warning',
    ETC: 'info',
  }

  // ------------------------------------------------
  // event
  // ------------------------------------------------
  const blankEvent = {
    title: '',
    start: '',
    end: '',
    start_formatted: '',
    end_formatted: '',
    duration: '',
    allDay: false,
    url: '',
    organizationalUnitsList: [],
    extendedProps: {
      calendar: '',
      guests: [],
      location: '',
      description: '',
      suggestionDateList: [],
      scheduleEventId: null,
      eventUrl: '',
      reminders: [],
      repeat: null,
      repeat_until: now.format('DD/MM/YYYY HH:mm'),
      invited: false,
      author_name: '',
      author_email: '',
    },
  }
  const event = ref(JSON.parse(JSON.stringify(blankEvent)))
  const eventToDelete = ref({calendar_id: null, schedule_id: null})
  const clearEventData = () => {
    event.value = JSON.parse(JSON.stringify(blankEvent))
  }

  // *===========================================================================---*
  // *--------- Calendar API Function/Utils --------------------------------------------*
  // Template Future Update: We might move this utils function in its own file
  // *===========================================================================---*

  // ------------------------------------------------
  // (UI) addEventInCalendar
  // ? This is useless because this just add event in calendar and not in our data
  // * If we try to call it on new event then callback & try to toggle from calendar we get two events => One from UI and one from data
  // ------------------------------------------------
  // const addEventInCalendar = eventData => {
  //   toast({
  //     component: ToastificationContent,
  //     position: 'bottom-right',
  //     props: {
  //       title: 'Event Added',
  //       icon: 'CheckIcon',
  //       variant: 'success',
  //     },
  //   })
  //   calendarApi.addEvent(eventData)
  // }

  // ------------------------------------------------
  // (UI) updateEventInCalendar
  // ------------------------------------------------
  const updateEventInCalendar = (updatedEventData, propsToUpdate, extendedPropsToUpdate) => {
    toast({
      component: ToastificationContent,
      props: {
        title: 'Evento atualizado',
        icon: 'CheckIcon',
        variant: 'success',
      },
    })

    let events = calendarApi.getEvents()
    events = events.filter(event => {
      return event.extendedProps.scheduleEventId === updatedEventData.id
    })

    // const existingEvent = calendarApi.getEventById(updatedEventData.id)

    events.forEach(existingEvent => {

      // --- Set event properties except date related ----- //
      // ? Docs: https://fullcalendar.io/docs/Event-setProp
      // dateRelatedProps => ['start', 'end', 'allDay']
      // eslint-disable-next-line no-plusplus
      for (let index = 0; index < propsToUpdate.length; index++) {
        const propName = propsToUpdate[index]
        const value = updatedEventData[propName]
        if (value !== undefined && value !== '' && value !== null) {
          existingEvent.setProp(propName, value)
        }
      }

      // Filter current suggestion
      let scheduleSuggestions = updatedEventData.schedule_suggestions
      scheduleSuggestions.forEach(scheduleSuggestion => {
        if (scheduleSuggestion.id === updatedEventData.id) {

          // --- Set date related props ----- //
          // ? Docs: https://fullcalendar.io/docs/Event-setDates
          existingEvent.setDates(scheduleSuggestion.start_at, scheduleSuggestion.finish_at, { allDay: updatedEventData.allDay })

        }
      })


      // --- Set event's extendedProps ----- //
      // ? Docs: https://fullcalendar.io/docs/Event-setExtendedProp
      // eslint-disable-next-line no-plusplus
      for (let index = 0; index < extendedPropsToUpdate.length; index++) {
        const propName = extendedPropsToUpdate[index]
        const value = updatedEventData[propName]
        if (value !== undefined && value !== '' && value !== null) {
          existingEvent.setExtendedProp(propName, value)
        }
      }

    })

  }

  // ------------------------------------------------
  // (UI) removeEventInCalendar
  // ------------------------------------------------
  const removeEventInCalendar = eventId => {
    toast({
      component: ToastificationContent,
      props: {
        title: 'Evento excluído',
        icon: 'TrashIcon',
        variant: 'danger',
      },
    })
    refetchEvents()
  }

  // ------------------------------------------------
  // grabEventDataFromEventApi
  // ? It will return just event data from fullCalendar's EventApi which is not required for event mutations and other tasks
  // ! You need to update below function as per your extendedProps
  // ------------------------------------------------
  const grabEventDataFromEventApi = eventApi => {
    const {
      id,
      title,
      start,
      end,
      start_formatted,
      end_formatted,
      duration,
      url,
      organizationalUnitsList,
      // eslint-disable-next-line object-curly-newline
      extendedProps: { calendar, guests, location, description, suggestionDateList, scheduleEventId, eventUrl, reminders, repeat, repeat_until, invited, author_email, author_name},
      allDay,
    } = eventApi

    return {
      id,
      title,
      start,
      end,
      start_formatted,
      end_formatted,
      duration,
      url,
      organizationalUnitsList,
      extendedProps: {
        calendar,
        guests,
        location,
        description,
        suggestionDateList,
        scheduleEventId,
        eventUrl,
        reminders,
        repeat,
        repeat_until,
        invited,
        author_email,
        author_name,
      },
      allDay,
    }
  }

  // ------------------------------------------------
  // addEvent
  // ------------------------------------------------
  const addEvent = eventData => {

    store.dispatch('calendar/addEvent', { event: eventData }).then(() => {
      // eslint-disable-next-line no-use-before-define
      console.log('Event add...')
      toast({
        component: ToastificationContent,
        props: {
          title: 'Agendamento criado com sucesso!',
          icon: 'CheckIcon',
          variant: 'success',
          timeout: 3000
        },
      })

      //Close side bar
      isEventHandlerSidebarActive.value = false

      refetchEvents()
    }).catch(() => {
      console.log('Event add error...')
      toast({
        component: ToastificationContent,
        props: {
          title: 'Houve algum problema ao adicionar o agendamento no servidor.',
          icon: 'AlertTriangleIcon',
          variant: 'danger',
          timeout: 3000
        },
      })
    })
  }

  // ------------------------------------------------
  // updateEvent
  // ------------------------------------------------
  const updateEvent = eventData => {

    eventData = {...eventData, url: eventData.extendedProps.eventUrl}
    return store.dispatch('calendar/updateEvent', { event: eventData }).then(updatedEvent => {
      const propsToUpdate = ['id', 'title', 'url']
      const extendedPropsToUpdate = ['calendar', 'guests', 'location', 'description', 'suggestionDateList', 'scheduleEventId', 'reminders', 'repeat', 'repeat_until']

      updateEventInCalendar(updatedEvent, propsToUpdate, extendedPropsToUpdate)
    }).then(() => {
      refetchEvents()
    })
  }

  const exclusionJustification = ref('')

  // ------------------------------------------------
  // removeEvent
  // ------------------------------------------------
  const removeEvent = (deletePayload) => {

    const scheduleEventId = eventToDelete.value.schedule_id || event.value.extendedProps.scheduleEventId
    const eventId = eventToDelete.value.calendar_id || event.value.id
    const reason = exclusionJustification.value

    if (eventId) {
      store.dispatch('calendar/removeEvent', { id: scheduleEventId, reason }).then(() => {
        removeEventInCalendar(eventId)
      }).then((resolved) => {
        if ('hide' in deletePayload && isFunction(deletePayload.hide)) {
          deletePayload.hide()
        }
        exclusionJustification.value = ''
      }, (error) => {
        exclusionJustification.value = ''
        console.info("Cannot delete, there was something in the way. 🌩️")
      })
    }
  }

  // ------------------------------------------------
  // refetchEvents
  // ------------------------------------------------
  const refetchEvents = () => {
    calendarApi.refetchEvents()
  }

  // ------------------------------------------------
  // Cache de últimos eventos e controle de fetch duplicado
  // ------------------------------------------------
  let lastEvents = []
  let lastFetchTime = 0
  let lastFetchKey = null

  const getFetchKey = (info) => {
    const viewType = info.view?.type || calendarApi?.view?.type || 'unknown'
    return `${viewType}-${info.startStr}-${info.endStr}`
  }

  const shouldExecuteFetch = (info) => {
    const now = Date.now()
    const currentKey = getFetchKey(info)
    const timeSinceLastFetch = now - lastFetchTime

    console.log(`🔍 shouldExecuteFetch: ${currentKey}`)
    console.log(`🕐 Tempo desde último fetch: ${timeSinceLastFetch}ms`)
    console.log(`🔑 Última chave: ${lastFetchKey}`)
    console.log(`🔑 Chave atual: ${currentKey}`)

    // Se é EXATAMENTE a mesma consulta em menos de 2 segundos, bloquear
    if (currentKey === lastFetchKey && timeSinceLastFetch < 2000) {
      console.log(`🚫 BLOQUEANDO fetch duplicado: ${currentKey} (${timeSinceLastFetch}ms)`)
      return false
    }

    // Atualizar controles ANTES de permitir
    lastFetchTime = now
    lastFetchKey = currentKey
    console.log(`✅ PERMITINDO fetch: ${currentKey}`)
    return true
  }

  // ------------------------------------------------
  // selectedCalendars
  // ------------------------------------------------
  const selectedCalendars = computed(() => store.state.calendar.selectedCalendars)

  watch(selectedCalendars, (newVal, oldVal) => {
    // Só refetch se realmente mudou e não é a primeira execução
    if (newVal !== oldVal && oldVal !== undefined && calendarApi) {
      console.log('📅 Calendários selecionados mudaram - fazendo refetch')
      refetchEvents()
    }
  })

  // ------------------------------------------------
  // selectedAuthor - watch para refetch quando mudar autor
  // ------------------------------------------------
  const selectedAuthor = computed(() => store.state.calendar.selectedAuthor)

  watch(selectedAuthor, (newVal, oldVal) => {
    // Evitar refetch desnecessário se o valor não mudou realmente ou é primeira execução
    if (newVal !== oldVal && oldVal !== undefined && calendarApi) {
      console.log('📅 Autor selecionado mudou - fazendo refetch')
      refetchEvents()
    }
  })

  // --------------------------------------------------------------------------------------------------
  // AXIOS: fetchEvents
  // * This will be called by fullCalendar to fetch events. Also this can be used to refetch events.
  // --------------------------------------------------------------------------------------------------
  const fetchEvents = (info, successCallback) => {
    // If there's no info => Don't make useless API call
    if (!info) return

    // Emitir evento de início do loading se disponível
    if (composition.emit) {
      composition.emit('loading-start')
    }

    // Controle anti-duplicação robusto
    // Não bloquear por chave idêntica quando a view é timeGrid/list,
    // pois o FullCalendar pode chamar novamente após ajustar o intervalo.
    const viewTypeForKey = info.view?.type || calendarApi?.view?.type || ''
    const allowDuplicateForView = viewTypeForKey.startsWith('timeGrid') || viewTypeForKey.startsWith('list')
    if (!allowDuplicateForView && !shouldExecuteFetch(info)) {
      console.log('❌ FETCH BLOQUEADO - mantendo eventos atuais (não chamando successCallback)')
      return
    }

    // Extrair filtros de data do FullCalendar info
    const filters = {}
    if (info.start && info.end) {
      let startDate = moment(info.start)
      let endDate = moment(info.end)

      // Otimizar período de busca baseado na visualização atual
      const currentView = calendarApi?.view?.type

      if (currentView === 'dayGridMonth') {
        // Visualização mensal: limitar ao mês atual apenas
        // FullCalendar inclui semanas completas, mas queremos só o mês
        const middleDate = moment(info.start).add(Math.floor(moment(info.end).diff(moment(info.start), 'days') / 2), 'days')
        startDate = middleDate.clone().startOf('month')
        endDate = middleDate.clone().endOf('month')
        console.log(`📅 Mês: ${startDate.format('YYYY-MM')} (${startDate.format('DD/MM')} - ${endDate.format('DD/MM')})`)

      } else if (currentView === 'timeGridWeek') {
        // Visualização semanal: usar a semana exata
        startDate = moment(info.start).startOf('day')
        endDate = moment(info.end).subtract(1, 'day').endOf('day') // FullCalendar end é exclusive
        console.log(`📅 Semana: ${startDate.format('DD/MM')} - ${endDate.format('DD/MM')}`)

      } else if (currentView === 'timeGridDay') {
        // Visualização diária: usar apenas o dia
        startDate = moment(info.start).startOf('day')
        endDate = moment(info.start).endOf('day')
        console.log(`📅 Dia: ${startDate.format('DD/MM/YYYY')}`)

      } else {
        // Outras visualizações: usar o período padrão
        console.log(`📅 Período padrão: ${startDate.format('DD/MM')} - ${endDate.format('DD/MM')}`)
      }

      // Converter datas para formato ISO (YYYY-MM-DD)
      filters.startDate = startDate.format('YYYY-MM-DD')
      filters.endDate = endDate.format('YYYY-MM-DD')
    }

    // Fetch Events from API endpoint
    store
      .dispatch('calendar/fetchEvents', {
        calendars: selectedCalendars.value,
        filters: filters
      })
      .then(response => {
        console.log(`📦 DADOS RECEBIDOS da API:`, response)
        console.log(`📊 Quantidade de eventos: ${response ? response.length : 'undefined'}`)

        // Remover filtro client-side por autor - agora é feito no backend
        composition.emit('fetched-events', response)

        // Atualizar cache
        lastEvents = Array.isArray(response) ? response : []

        if (successCallback) {
          console.log(`✅ Chamando successCallback com ${response ? response.length : 0} eventos`)
          successCallback(response)
          console.log(`✅ successCallback executado - FullCalendar deve renderizar agora`)

          // Forçar re-render em views timeGrid/list (alguns navegadores podem precisar)
          const vt = calendarApi?.view?.type || ''
          if (vt.startsWith('timeGrid') || vt.startsWith('list')) {
            setTimeout(() => {
              if (calendarApi?.updateSize) {
                console.log('📐 updateSize() após successCallback (timeGrid/list)')
                calendarApi.updateSize()
              }
              if (calendarApi?.refetchEvents) {
                console.log('🔁 refetchEvents() imediato para garantir sincronização')
                calendarApi.refetchEvents()
              }
            }, 0)
          }
        } else {
          console.log(`❌ successCallback não definido!`)
        }

        // Emitir evento de fim do loading
        if (composition.emit) {
          composition.emit('loading-end')
        }
      })
      .catch(() => {
        console.log('Houve algum problema ao buscar os eventos no servidor.')

        // Emitir evento de fim do loading mesmo em caso de erro
        if (composition.emit) {
          composition.emit('loading-end')
        }

        /*
        toast({
          component: ToastificationContent,
          props: {
            title: 'Houve algum problema ao buscar os eventos no servidor.',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
        */
      })
  }

  const fetchCalendarTypes = (successCallback) => {
    // Fetch Events from API endpoint
    store
      .dispatch('calendar/fetchCalendarTypes', {})
      .then(response => {
        successCallback(response.data)
      })
      .catch(() => {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Houve algum problema ao buscar os tipos de eventos no servidor.',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
  }

  // ------------------------------------------------------------------------
  // calendarOptions
  // * This isn't considered in UI because this is the core of calendar app
  // ------------------------------------------------------------------------
  const calendarOptions = ref({
    plugins: [dayGridPlugin, interactionPlugin, timeGridPlugin, listPlugin],
    initialView: 'dayGridMonth',
    headerToolbar: {
      start: 'sidebarToggle, prev,todayButton,next, title',
      end: 'dayGridMonth,timeGridWeek,timeGridDay,listMonth',
    },
    events: fetchEvents,

    /*
      Enable dragging and resizing event
      ? Docs: https://fullcalendar.io/docs/editable
    */
    editable: false,

    /*
      Enable resizing event from start
      ? Docs: https://fullcalendar.io/docs/eventResizableFromStart
    */
    eventResizableFromStart: false,

    /*
      Automatically scroll the scroll-containers during event drag-and-drop and date selecting
      ? Docs: https://fullcalendar.io/docs/dragScroll
    */
    dragScroll: false,

    /*
      Max number of events within a given day
      ? Docs: https://fullcalendar.io/docs/dayMaxEvents
    */
    dayMaxEvents: 2,

    /*
      Determines if day names and week names are clickable
      ? Docs: https://fullcalendar.io/docs/navLinks
    */
    navLinks: false,

    eventClassNames({ event: calendarEvent }) {
      // eslint-disable-next-line no-underscore-dangle
      const calendarId = calendarEvent._def.extendedProps.calendar
      let colorName = 'primary'

      // Mapeamento correto baseado nos tipos do banco de dados:
      // ID 1: "Reunião" (cor: #7367f0 - roxo) → purple
      // ID 2: "Evento" (cor: #28c76f - verde) → success
      // ID 3: "Lembrete" (cor: #ff9f43 - laranja) → warning
      if(calendarId === 1){
        colorName = 'purple'  // Reunião - roxo (agora usando purple)
      }else if(calendarId === 2){
        colorName = 'success'  // Evento - verde
      }else if(calendarId === 3){
        colorName = 'warning'  // Lembrete - laranja
      }

      return [
        // Background Color
        `bg-light-${colorName}`,
      ]
    },
    eventClick({ event: clickedEvent }) {
      // * Only grab required field otherwise it goes in infinity loop
      // ! Always grab all fields rendered by form (even if it get `undefined`) otherwise due to Vue3/Composition API you might get: "object is not extensible"
      event.value = grabEventDataFromEventApi(clickedEvent)

      // console.log("The event has been clicked!", event)

      // eslint-disable-next-line no-use-before-define
      isEventHandlerSidebarActive.value = true
    },

    customButtons: {
      sidebarToggle: {
        // --- This dummy text actual icon rendering is handled using SCSS ----- //
        text: 'sidebar',
        click() {
          // eslint-disable-next-line no-use-before-define
          isCalendarOverlaySidebarActive.value = !isCalendarOverlaySidebarActive.value
        },
      },
      todayButton: {
        text: 'today', // Texto usado para CSS (não visível)
        hint: 'Voltar para o mês atual',
        click() {
          // Navegar para o mês atual
          const today = new Date()
          calendarApi.gotoDate(today)

          // Toast opcional para feedback visual
          toast({
            component: ToastificationContent,
            props: {
              title: 'Navegado para o mês atual',
              icon: 'CalendarIcon',
              variant: 'info',
              timeout: 2000
            },
          })
        },
      },
    },

    dateClick(info) {
      console.log('dateClick...')
      /*
        ! Vue3 Change
        Using Vue.set isn't working for now so we will try to check reactivity in Vue 3 as it can handle this automatically
        ```
        event.value.start = info.date
        ```
      */
      event.value = JSON.parse(JSON.stringify(Object.assign(event.value, { start: info.date })))
      // eslint-disable-next-line no-use-before-define
      isEventHandlerSidebarActive.value = true
    },

    /*
      Handle event drop (Also include dragged event)
      ? Docs: https://fullcalendar.io/docs/eventDrop
      ? We can use `eventDragStop` but it doesn't return updated event so we have to use `eventDrop` which returns updated event
    */
    eventDrop({ event: droppedEvent }) {
      updateEvent(grabEventDataFromEventApi(droppedEvent))
    },

    /*
      Handle event resize
      ? Docs: https://fullcalendar.io/docs/eventResize
    */
    eventResize({ event: resizedEvent }) {
      updateEvent(grabEventDataFromEventApi(resizedEvent))
    },

    // Get direction from app state (store)
    direction: computed(() => (store.state.appConfig.isRTL ? 'rtl' : 'ltr')),
    rerenderDelay: 350,

    locales: [ptBRLocale],
    locale: 'pt-BR', // the initial locale

    // Evento disparado sempre que há mudança de visualização ou navegação
    datesSet(info) {
      // Este evento é disparado quando:
      // - Mudança de mês (anterior/próximo)
      // - Mudança de visualização (mês → semana → dia)
      // - Clique no botão "Hoje"
      console.log(`📅 datesSet disparado: ${info.view.type} | ${info.startStr} → ${info.endStr}`)
      // Importante: não forçar refetch aqui. O FullCalendar já executa events: fetchEvents
      // e refetch manual pode cancelar o fetch em andamento, causando tela vazia.
    }
  })

  // ------------------------------------------------------------------------

  // *===============================================---*
  // *--------- UI ---------------------------------------*
  // *===============================================---*

  const isEventHandlerSidebarActive = ref(false)

  const isCalendarOverlaySidebarActive = ref(false)

  return {
    refCalendar,
    isCalendarOverlaySidebarActive,
    calendarOptions,
    event,
    eventToDelete,
    exclusionJustification,
    clearEventData,
    addEvent,
    updateEvent,
    removeEvent,
    refetchEvents,
    fetchEvents,
    fetchCalendarTypes,

    // ----- UI ----- //
    isEventHandlerSidebarActive,
  }
}
